import React, { useState, useEffect, useRef } from 'react';
import CustomizedSnackbars from "../components/CustomizedSnackbar";
import { CircularProgress } from "@material-ui/core";
import Axios from "axios";

function useReveal(threshold = 0.15) {
	const ref = useRef(null);
	useEffect(() => {
		if (!ref.current) return;
		const el = ref.current;
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					el.style.opacity = "1";
					el.style.transform = "translateY(0)";
					observer.disconnect();
				}
			},
			{ threshold }
		);
		observer.observe(el);
		return () => observer.disconnect();
	}, [threshold]);
	return ref;
}

export const Home2ndOpinion = () => {
	const source = require("../data/2ndOpinion.json");
	const data = source["DIAGNOSIS"];

	const [formData, setFormData] = useState({ name: "", email: "", phone: "", message: "" });
	const [open, setOpen] = useState(false);
	const [message, setMessage] = useState("");
	const [severity, setSeverity] = useState("");
	const [disableButton, setDisableButton] = useState(false);

	const formRef = useReveal();
	const stepsRef = useReveal();
	const cardsRef = useReveal();

	const formatPhone = (phone) => {
		if (phone[0] === "+") return phone;
		return "+234" + phone;
	};

	const handleChange = (e) => {
		setFormData({
			...formData,
			[e.target.name]: e.target.name === "phone" ? formatPhone(e.target.value) : e.target.value,
		});
	};

	const handleClose = (event, reason) => {
		if (reason === "clickaway") return;
		setOpen(false);
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		setDisableButton(true);
		Axios.post("https://asess-econsult.herokuapp.com/api/v1/second-opinion/book_appointment/", formData, {})
			.then(() => {
				setDisableButton(false);
				setMessage("Your booking has been sent successfully. Please check your email address now to fill out the PATIENT MEDICAL FORM!");
				setSeverity("success");
				setOpen(true);
			})
			.catch((err) => {
				console.log(err);
				setDisableButton(false);
				setMessage("Sorry, there was an error while trying to send your booking. Please check your network settings and try again.");
				setSeverity("error");
				setOpen(true);
			});
	};

	const steps = [
		{
			icon: "fas fa-phone-alt",
			text: "Start by calling our doctors on call service at 08130790883",
		},
		{
			icon: "fas fa-user-plus",
			text: "Activate your 2nd medical opinion free membership and one of our doctors will connect with you",
		},
		{
			icon: "fas fa-video",
			text: "Connect with a specialist by phone or video at your convenience via the eConsult platform",
		},
	];

	return (
		<>
			{/* ── Appointment Section ── */}
			<section
				id="appointment"
				style={{
					padding: "6rem 0",
					background: "linear-gradient(180deg, #f0f9f9 0%, #ffffff 100%)",
					position: "relative",
					overflow: "hidden",
				}}
			>
				{/* Blobs */}
				<div style={{ position: "absolute", width: 500, height: 500, background: "rgba(0,184,184,0.08)", borderRadius: "50%", filter: "blur(80px)", top: -150, right: -150, pointerEvents: "none" }} />
				<div style={{ position: "absolute", width: 350, height: 350, background: "rgba(55,81,75,0.07)", borderRadius: "50%", filter: "blur(60px)", bottom: -80, left: -100, pointerEvents: "none" }} />

				<div className="container" style={{ position: "relative", zIndex: 1 }}>
					<div className="row justify-content-center" style={{ marginBottom: "3rem" }}>
						<div className="col-12 col-lg-8 text-center">
							<div className="section-tag">Book an Appointment</div>
							<div className="accent-line accent-line-center" style={{ margin: "1rem auto 1.5rem" }} />
							<h2 className="section-title-modern">
								No worries. No travel. <span>Get Care Now.</span>
							</h2>
							<p style={{ color: "#6b7280", lineHeight: 1.8, maxWidth: 560, margin: "0 auto" }}>
								Get your medical care by phone or video from the comfort of your home.
							</p>
						</div>
					</div>

					<div className="row g-4 align-items-stretch">
						{/* Steps */}
						<div className="col-12 col-lg-5">
							<div
								ref={stepsRef}
								className="card-animate-in"
								style={{ height: "100%" }}
							>
								<div
									style={{
										background: "#ffffff",
										borderRadius: 24,
										padding: "2.5rem",
										boxShadow: "0 20px 60px rgba(0,0,0,0.08)",
										border: "1px solid rgba(0,184,184,0.1)",
										height: "100%",
									}}
								>
									<h4 style={{ fontWeight: 700, color: "#1a2332", marginBottom: "2rem", fontSize: "1.3rem" }}>
										How it works
									</h4>

									{steps.map((step, i) => (
										<div
											key={i}
											style={{
												display: "flex",
												alignItems: "flex-start",
												gap: "1.25rem",
												padding: "1.25rem 0",
												borderBottom: i < steps.length - 1 ? "1px solid rgba(0,0,0,0.06)" : "none",
											}}
										>
											{/* Step number + icon */}
											<div style={{ position: "relative", flexShrink: 0 }}>
												<div
													style={{
														width: 48,
														height: 48,
														borderRadius: 14,
														background: "linear-gradient(135deg, #37514B 0%, #00b8b8 100%)",
														display: "flex",
														alignItems: "center",
														justifyContent: "center",
														color: "#fff",
														fontSize: "1rem",
														boxShadow: "0 6px 20px rgba(0,184,184,0.3)",
													}}
												>
													<i className={step.icon} />
												</div>
												<div
													style={{
														position: "absolute",
														top: -8,
														right: -8,
														width: 20,
														height: 20,
														background: "#37514B",
														color: "#fff",
														borderRadius: "50%",
														fontSize: "0.65rem",
														fontWeight: 700,
														display: "flex",
														alignItems: "center",
														justifyContent: "center",
													}}
												>
													{i + 1}
												</div>
											</div>
											<p style={{ margin: 0, color: "#374151", lineHeight: 1.7, fontSize: "0.95rem", fontWeight: 500 }}>
												{step.text}
											</p>
										</div>
									))}

									{/* Trust indicators */}
									<div
										style={{
											marginTop: "2rem",
											padding: "1.25rem",
											background: "linear-gradient(135deg, rgba(55,81,75,0.06) 0%, rgba(0,184,184,0.06) 100%)",
											borderRadius: 16,
											display: "flex",
											alignItems: "center",
											gap: 12,
										}}
									>
										<i className="fas fa-shield-alt" style={{ fontSize: "1.5rem", color: "#37514B" }} />
										<div>
											<div style={{ fontWeight: 700, fontSize: "0.85rem", color: "#1a2332" }}>
												Secure & Confidential
											</div>
											<div style={{ fontSize: "0.78rem", color: "#6b7280" }}>
												Your health data is fully protected
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>

						{/* Appointment Form */}
						<div className="col-12 col-lg-7">
							<div
								ref={formRef}
								className="card-animate-in"
								style={{ height: "100%", animationDelay: "0.15s" }}
							>
								<div
									style={{
										background: "linear-gradient(135deg, #253832 0%, #1a4a42 60%, #0d2d2d 100%)",
										borderRadius: 24,
										padding: "2.5rem",
										boxShadow: "0 30px 80px rgba(0,0,0,0.2)",
										position: "relative",
										overflow: "hidden",
										height: "100%",
									}}
								>
									{/* Decorative circles */}
									<div style={{ position: "absolute", top: -60, right: -60, width: 200, height: 200, background: "rgba(0,184,184,0.12)", borderRadius: "50%", pointerEvents: "none" }} />
									<div style={{ position: "absolute", bottom: -80, left: -40, width: 250, height: 250, background: "rgba(0,184,184,0.06)", borderRadius: "50%", pointerEvents: "none" }} />

									<div style={{ position: "relative", zIndex: 1 }}>
										<div
											style={{
												display: "inline-flex",
												alignItems: "center",
												gap: 8,
												background: "rgba(0,184,184,0.2)",
												border: "1px solid rgba(0,184,184,0.35)",
												color: "#4dd9d9",
												padding: "0.35rem 1rem",
												borderRadius: 50,
												fontSize: "0.78rem",
												fontWeight: 600,
												letterSpacing: 1,
												textTransform: "uppercase",
												marginBottom: "1.25rem",
											}}
										>
											<span style={{ width: 7, height: 7, background: "#00b8b8", borderRadius: "50%", display: "inline-block" }} />
											Free Consultation
										</div>

										<h3 style={{ color: "#fff", fontWeight: 700, marginBottom: "0.4rem", fontSize: "1.5rem" }}>
											Book Appointment
										</h3>
										<p style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.88rem", marginBottom: "2rem" }}>
											Fill in your details and we'll get back to you within 24 hours
										</p>

										<form autoComplete="off" onSubmit={handleSubmit} className="dark-form">
											{[
												{ name: "name", type: "text", placeholder: "Your full name", icon: "fas fa-user" },
												{ name: "email", type: "email", placeholder: "Email address", icon: "fas fa-envelope" },
												{ name: "phone", type: "tel", placeholder: "Phone number (e.g. 08012345678)", icon: "fas fa-phone" },
											].map((field) => (
												<div key={field.name} style={{ position: "relative", marginBottom: 0 }}>
													<div
														style={{
															position: "absolute",
															left: 14,
															top: "50%",
															transform: "translateY(-50%)",
															color: "rgba(255,255,255,0.45)",
															fontSize: "0.85rem",
															pointerEvents: "none",
															zIndex: 2,
														}}
													>
														<i className={field.icon} />
													</div>
													<input
														type={field.type}
														name={field.name}
														placeholder={field.placeholder}
														onChange={handleChange}
														defaultValue={formData[field.name]}
														required
													/>
												</div>
											))}

											<div style={{ position: "relative", marginBottom: 0 }}>
												<textarea
													name="message"
													rows={3}
													placeholder="How can we help you? Describe your medical concern..."
													onChange={handleChange}
													value={formData.message}
													required
													className="no-icon"
												/>
											</div>

											<button
												type="submit"
												disabled={disableButton}
												style={{
													width: "100%",
													padding: "1rem",
													background: disableButton
														? "rgba(255,255,255,0.2)"
														: "linear-gradient(135deg, #37514B 0%, #00b8b8 100%)",
													border: "none",
													borderRadius: 12,
													color: "#fff",
													fontSize: "1rem",
													fontWeight: 700,
													cursor: disableButton ? "not-allowed" : "pointer",
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													gap: 10,
													transition: "all 0.3s ease",
													boxShadow: disableButton ? "none" : "0 8px 25px rgba(0,184,184,0.35)",
												}}
												onMouseEnter={(e) => {
													if (!disableButton) {
														e.currentTarget.style.transform = "translateY(-2px)";
														e.currentTarget.style.boxShadow = "0 12px 35px rgba(0,184,184,0.5)";
													}
												}}
												onMouseLeave={(e) => {
													e.currentTarget.style.transform = "translateY(0)";
													e.currentTarget.style.boxShadow = "0 8px 25px rgba(0,184,184,0.35)";
												}}
											>
												{disableButton ? (
													<>
														<CircularProgress size={20} style={{ color: "#fff" }} />
														Processing, please wait...
													</>
												) : (
													<>
														<i className="fas fa-calendar-check" />
														Book Appointment
													</>
												)}
											</button>

											<div className="form-results d-none" />
										</form>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<CustomizedSnackbars notify={open} closeNotify={handleClose} message={message} severity={severity} />
			</section>

			{/* ── Diagnosis Specialty Cards ── */}
			<section
				style={{
					padding: "4rem 0 6rem",
					background: "#ffffff",
					position: "relative",
				}}
			>
				<div
					style={{
						position: "absolute",
						top: 0,
						left: 0,
						right: 0,
						height: 4,
						background: "linear-gradient(90deg, #37514B, #00b8b8, #37514B)",
						backgroundSize: "200% auto",
						animation: "borderFlow 3s linear infinite",
					}}
				/>
				<div className="container">
					<div className="row justify-content-center mb-5">
						<div className="col-12 col-lg-7 text-center">
							<div className="section-tag">Medical Specialties</div>
							<div className="accent-line accent-line-center" style={{ margin: "1rem auto 1.5rem" }} />
							<h2 className="section-title-modern">
								Our Medical &amp; Surgical <span>Specialists</span>
							</h2>
							<p style={{ color: "#6b7280", lineHeight: 1.8 }}>
								Our board-certified specialists can help you clarify your diagnosis and explore the best treatment options.
							</p>
						</div>
					</div>

					<div
						ref={cardsRef}
						className="row card-animate-in"
					>
						{Object.entries(data).map((item, i) => (
							<div
								key={"opinion-" + i}
								className="col-12 col-sm-6 col-lg-4"
								style={{ marginBottom: '1.5rem' }}
							>
								<div
									style={{
										borderRadius: 20,
										overflow: "hidden",
										boxShadow: "0 15px 50px rgba(0,0,0,0.12)",
										position: "relative",
										transition: "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
										cursor: "default",
										height: 320,
									}}
									onMouseEnter={(e) => {
										e.currentTarget.style.transform = "translateY(-10px)";
										e.currentTarget.style.boxShadow = "0 30px 80px rgba(0,0,0,0.2)";
										e.currentTarget.querySelector(".card-hover-content").style.opacity = "1";
										e.currentTarget.querySelector(".card-hover-content").style.transform = "translateY(0)";
										e.currentTarget.querySelector(".card-number-badge").style.opacity = "0";
										const img = e.currentTarget.querySelector("img");
										if (img) img.style.transform = "scale(1.1)";
									}}
									onMouseLeave={(e) => {
										e.currentTarget.style.transform = "translateY(0)";
										e.currentTarget.style.boxShadow = "0 15px 50px rgba(0,0,0,0.12)";
										e.currentTarget.querySelector(".card-hover-content").style.opacity = "0";
										e.currentTarget.querySelector(".card-hover-content").style.transform = "translateY(20px)";
										e.currentTarget.querySelector(".card-number-badge").style.opacity = "1";
										const img = e.currentTarget.querySelector("img");
										if (img) img.style.transform = "scale(1)";
									}}
								>
									{/* Image */}
									<img
										src={require(`../media/images/${item[1][1]}`).default}
										alt={item[0]}
										style={{
											width: "100%",
											height: "100%",
											objectFit: "cover",
											transition: "transform 0.6s ease",
											position: "absolute",
											inset: 0,
										}}
									/>

									{/* Gradient overlay */}
									<div
										style={{
											position: "absolute",
											inset: 0,
											background: "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.2) 50%, transparent 100%)",
										}}
									/>

									{/* Hover gradient */}
									<div
										style={{
											position: "absolute",
											inset: 0,
											background: "linear-gradient(135deg, rgba(55,81,75,0.85) 0%, rgba(0,184,184,0.7) 100%)",
											opacity: 0,
											transition: "opacity 0.4s ease",
										}}
										className="card-gradient-overlay"
									/>

									{/* Number badge */}
									<div
										className="card-number-badge"
										style={{
											position: "absolute",
											top: 20,
											left: 20,
											background: "rgba(0,184,184,0.9)",
											color: "#fff",
											width: 36,
											height: 36,
											borderRadius: "50%",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											fontWeight: 700,
											fontSize: "0.85rem",
											backdropFilter: "blur(8px)",
											transition: "opacity 0.3s ease",
										}}
									>
										{String(i + 1).padStart(2, "0")}
									</div>

									{/* Bottom title (always visible) */}
									<div
										style={{
											position: "absolute",
											bottom: 0,
											left: 0,
											right: 0,
											padding: "1.5rem",
										}}
									>
										<h5
											style={{
												color: "#fff",
												fontWeight: 700,
												margin: 0,
												fontSize: "1.05rem",
												textShadow: "0 2px 8px rgba(0,0,0,0.4)",
												textTransform: "capitalize",
											}}
										>
											{item[0]}
										</h5>
									</div>

									{/* Hover content */}
									<div
										className="card-hover-content"
										style={{
											position: "absolute",
											inset: 0,
											display: "flex",
											flexDirection: "column",
											justifyContent: "center",
											alignItems: "center",
											padding: "2rem",
											textAlign: "center",
											opacity: 0,
											transform: "translateY(20px)",
											transition: "opacity 0.4s ease, transform 0.4s ease",
											zIndex: 2,
										}}
									>
										<h5
											style={{
												color: "#fff",
												fontWeight: 700,
												marginBottom: "1rem",
												fontSize: "1.1rem",
												textTransform: "capitalize",
											}}
										>
											{item[0]}
										</h5>
										<p
											style={{
												color: "rgba(255,255,255,0.88)",
												fontSize: "0.85rem",
												lineHeight: 1.7,
												margin: 0,
											}}
										>
											{item[1][0]}
										</p>
									</div>
								</div>
							</div>
						))}
					</div>
				</div>
			</section>
		</>
	);
};
